import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// MUI Icons
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// Custom Components
import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import useFormController from '@/components/useFormController';
import GridContainer from '@/ui/Grid/GridContainer';
import FormContent from '@/components/FormContent';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';

import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import UserContext from '@/context/UserContext/UserContext';
import DataController from '@/lib/DataController';
import modelComments from '@/models/comments';

const CommentAdministrationDialog = (props: IDialogProps) => {
  const { onClose, initialRecord, level, customPath = 'comments/administration' } = props;

  const { t } = useTranslation();
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const userContext = useContext(UserContext);

  const dc = new DataController(modelComments);

  const close = (result: any) => {
    if (onClose) {
      onClose(result);
    }

    hideDialog(level);
  };

  const {
    record,
    validation,
    dataChanged,
    fields,
    pickerDataControllers,
    onFieldChange,
    doUpdate,
  } = useFormController({
    mode: 'update',
    form: 'administration',
    dc,
    onClose: close,
    initialRecord,
    customPath,
    pickerApiPathParams: {
      tenant_id: userContext?.tenantId,
    },
  });

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleClose = () => {
    close({ success: false });
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            showNotification(t('messages.comment_update'), 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            showNotification(result.error, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  return (
    <DraggableDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <ManageAccountsIcon sx={{ transform: 'scale(1.1)' }} />
          <DialogToolbarHeading>{t('common.administration')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames('administration')}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode="update"
            columns={1}
            pickerDataControllers={pickerDataControllers}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={!dataChanged} />
      </DialogActions>
    </DraggableDialog>
  );
};

export default CommentAdministrationDialog;
